import { Task } from "./tasks"

const cableMeta = `
For Xfinity, we need the following params to complete a call:
- ssn last 4 digits
- credit card last 4 digits (required)
- address zip
- account phone number
- account holder name
- service address
- service address line 2 (optional)
- service city
- service state
- service zip

For Spectrum, we need the following params to complete a call:
- security code - found on your bill (required)
- address zip
- account phone number
- account holder name
- service address
- service address line 2 (optional)
- service city
- service state
- service zip

For Cox, we need the following params to complete a call:
- PIN - found on your  bill (required)
- address zip
- account phone number
- account holder name
- service address
- service address line 2 (optional)
- service city
- service state
- service zip
`

export const tasks: Record<string, Task[]> = {
  "Financial & Bills": [
    new Task({
      id: "cable-bill",
      title: "Lower Your Cable Bill",
      description: "Get a better rate on your cable service with AI negotiation",
      additional_metadata: cableMeta
    }),
    new Task({
      id: "late-fee",
      title: "Remove Credit Card Late Fee",
      description: "Challenge and remove unfair credit card charges"
    }),
    new Task({
      id: "car-insurance",
      title: "Reduce Car Insurance Premium",
      description: "Find better rates on your auto insurance"
    }),
    new Task({
      id: "medical-bill",
      title: "Dispute Medical Bill",
      description: "Challenge unexpected medical charges"
    }),

    new Task({
      id: "audit-utility",
      title: "Audit Utility Bill for Overcharges",
      description: "Check monthly bills for hidden fees or suspicious charges"
    }),
    new Task({
      id: "split-bill",
      title: "Coordinate Splitting a Shared Bill",
      description: "Reach out to roommates or friends to coordinate payment splits"
    }),
    new Task({
      id: "bank-fee-waiver",
      title: "Request Monthly Bank Fee Waiver",
      description: "Negotiate waiving or reducing monthly bank service fees"
    }),
    new Task({
      id: "mortgage-rate",
      title: "Renegotiate Mortgage Rate",
      description: "See if your lender can offer a better interest rate"
    }),
    new Task({
      id: "student-loan",
      title: "Request Student Loan Payment Extension",
      description: "Ask about grace periods or reduced-rate payment plans"
    }),
    new Task({
      id: "internet-bundle",
      title: "Combine Internet and Phone for Bundle Pricing",
      description: "Explore better bundle deals for your services",
      additional_metadata: cableMeta
    }),
    new Task({
      id: "service-cancellation",
      title: "Negotiate Cancellation Fees",
      description: "Attempt to reduce or eliminate penalty fees for early service termination"
    }),
    new Task({
      id: "tax-extension",
      title: "Schedule a Tax Payment Plan/Extension",
      description: "Coordinate with tax authorities to avoid penalties"
    }),
    new Task({
      id: "insurance-deduction",
      title: "Verify Insurance Deduction Accuracy",
      description: "Call your insurer to ensure correct monthly deductions"
    }),
    new Task({
      id: "credit-limit",
      title: "Request Higher Credit Card Limit",
      description: "Negotiate an increase in your credit limit for big expenses"
    }),
    new Task({
      id: "refund-exchange",
      title: "Request Refund on Overpaid Taxes",
      description: "Follow up on a delayed or partial tax refund from the state or IRS"
    }),
    new Task({
      id: "utility-payment-plan",
      title: "Set Up Utility Payment Plan",
      description: "Avoid disconnection by spreading out overdue bills"
    }),
    new Task({
      id: "loan-payoff",
      title: "Get Loan Payoff Quote",
      description: "Obtain an official payoff amount and final due date"
    }),
    new Task({
      id: "overdraft-fee",
      title: "Negotiate Overdraft Fee Removal",
      description: "Remove recent overdraft charges to lower your banking costs"
    }),
    new Task({
      id: "mobile-plan",
      title: "Lower Mobile Phone Plan Rate",
      description: "Reach out to your carrier for promotional plans or loyalty discounts"
    }),
    new Task({
      id: "loan-forbearance",
      title: "Request Loan Forbearance",
      description: "Pause or reduce your loan payments during hardship"
    }),
    new Task({
      id: "83b-election",
      title: "File 83(b) Election Phone Inquiry",
      description:
        "Call the IRS or an accountant to confirm deadlines and steps needed to properly file an 83(b) election"
    }),
    new Task({
      id: "casino-taxes",
      title: "File Correction on Inaccurate Gambling Winnings",
      description:
        "Contact the casino or tax preparer to verify reported winnings or adjust any discrepancies in tax forms"
    }),
    new Task({
      id: "close-bank-accounts",
      title: "Terminate Unused Checking Account",
      description: "Coordinate final balance transfers and ensure any automatic payments are switched before closure"
    }),
    new Task({
      id: "fight-bank-fees",
      title: "Challenge Overdraft or Monthly Service Charges",
      description: "Negotiate a courtesy reversal by highlighting good account standing or banking loyalty"
    }),
    new Task({
      id: "help-with-bills",
      title: "Arrange Payment Extension with Utility Provider",
      description: "Negotiate a new due date or partial payment plan to avoid disconnection"
    }),
    new Task({
      id: "help-with-rent",
      title: "Negotiate Late Fee or Partial Payment with Landlord",
      description: "Explore flexible arrangements for monthly rent when funds are tight"
    }),
    new Task({
      id: "inflation-pay",
      title: "Ask Employer for Inflation-Based Wage Increase",
      description: "Present updated cost-of-living data as part of a salary negotiation call"
    }),
    new Task({
      id: "military-protection",
      title: "Notify Service Obligations for Loan Deferment",
      description: "Provide lenders with official documentation for active duty or protective rates"
    }),
    new Task({
      id: "unclaimed-money",
      title: "Check with State Treasury for Unclaimed Funds",
      description: "Call the state treasury office to see if the user has old deposits, escrow checks, or refunds"
    }),
    new Task({
      id: "401k-rollover",
      title: "Initiate 401k Rollover Process",
      description: "Contact previous employer's plan administrator to start retirement account transfer"
    }),
    new Task({
      id: "crypto-exchange-unlock",
      title: "Unlock Frozen Crypto Exchange Account",
      description: "Contact exchange support to verify identity and restore access"
    }),
    new Task({
      id: "hsa-reimbursement",
      title: "Process HSA Reimbursement Claims",
      description: "Submit receipts and follow up on health savings account claims"
    }),
    new Task({
      id: "payroll-error",
      title: "Report Paycheck Discrepancy",
      description: "Contact HR or payroll provider about missing or incorrect payments"
    }),
    new Task({
      id: "pension-verification",
      title: "Verify Pension Payment Status",
      description: "Check on delayed or missing pension distributions"
    }),
    new Task({
      id: "wire-transfer-tracking",
      title: "Track International Wire Transfer",
      description: "Follow up with bank on pending or delayed wire transfers"
    }),
    new Task({
      id: "investment-fraud",
      title: "Report Investment Scam",
      description: "Contact regulatory authorities about suspicious investment schemes"
    }),
    new Task({
      id: "ira-distribution",
      title: "Request Early IRA Distribution",
      description: "Process hardship withdrawal from retirement account"
    })
  ],
  "Healthcare & Wellness": [
    new Task({
      id: "dental",
      title: "Book Dental Cleaning",
      description: "Schedule your next dental appointment"
    }),
    new Task({
      id: "vet",
      title: "Schedule Vet Visit",
      description: "Book an appointment for your pet"
    }),
    new Task({
      id: "vaccine",
      title: "Book Vaccine Appointment",
      description: "Schedule your next vaccination"
    }),
    new Task({
      id: "specialist-referral",
      title: "Obtain Specialist Referral",
      description: "Call primary care to schedule or confirm a referral for specialized care"
    }),
    new Task({
      id: "rx-refill",
      title: "Request Prescription Refill",
      description: "Phone your pharmacy to handle medication refills or transfers"
    }),
    new Task({
      id: "eye-exam",
      title: "Schedule Eye Exam",
      description: "Arrange an appointment for vision check-ups or new glasses"
    }),
    new Task({
      id: "insurance-claim",
      title: "Follow Up on Insurance Claim",
      description: "Confirm coverage status or dispute claim denials"
    }),
    new Task({
      id: "therapy-session",
      title: "Book Therapy Session",
      description: "Coordinate appointment times with a counselor or therapist"
    }),
    new Task({
      id: "nutritionist",
      title: "Schedule Nutritionist Appointment",
      description: "Plan a call and set up dietary consultation or meal planning"
    }),
    new Task({
      id: "lab-tests",
      title: "Arrange Routine Lab Tests",
      description: "Confirm location, fasting requirements, and scheduling for lab work"
    }),
    new Task({
      id: "telehealth-setup",
      title: "Set Up Telehealth Consultation",
      description: "Coordinate virtual healthcare calls for convenience or remote locations"
    }),
    new Task({
      id: "dentist-insurance-coverage",
      title: "Confirm Dentist Insurance Coverage",
      description: "Verify out-of-pocket costs before treatments or check-ups"
    }),
    new Task({
      id: "therapeutic-massage",
      title: "Book Physical Therapy Massage",
      description: "Schedule a specialized treatment session for pain relief"
    }),
    new Task({
      id: "mental-health-group",
      title: "Enroll in Mental Health Group Session",
      description: "Find group therapy sessions for supportive care"
    }),
    new Task({
      id: "imaging-study",
      title: "Book Imaging Study (MRI/CT)",
      description: "Get an appointment for diagnostic imaging procedures"
    }),
    new Task({
      id: "home-care",
      title: "Arrange In-Home Care Services",
      description: "Set up visits for elderly or post-operative patients at home"
    }),
    new Task({
      id: "stress-test",
      title: "Schedule Cardiac Stress Test",
      description: "Book an appointment for critical heart health screenings"
    }),
    new Task({
      id: "therapy-billing",
      title: "Dispute Therapy Billing Error",
      description: "Review incorrect or unexpected charges for therapy sessions"
    }),
    new Task({
      id: "hearing-test",
      title: "Book Hearing Assessment",
      description: "Arrange hearing test with an audiologist"
    }),
    new Task({
      id: "free-dental",
      title: "Locate Low-Cost or Free Dental Clinic",
      description: "Check availability or referral requirements at community health centers"
    }),
    new Task({
      id: "emotional-support-animal",
      title: "Request ESA Letter from Mental Health Professional",
      description: "Contact a provider on behalf of the user to set up an evaluation or finalize an ESA document"
    }),
    new Task({
      id: "fight-medical-fraud",
      title: "Report Suspicious Billing or Insurance Claims",
      description: "Contact relevant health authorities or hospital billing to investigate fraudulent activities"
    }),
    new Task({
      id: "request-medical-records",
      title: "Obtain Copy of Personal or Family Health Records",
      description: "Comply with HIPAA guidelines, confirm release forms, and check fees or timeline"
    }),
    new Task({
      id: "clinical-trials",
      title: "Sign Up for Clinical Research Study",
      description: "Call a research facility to inquire about eligibility, time commitments, and compensation"
    }),
    new Task({
      id: "medical-device-support",
      title: "Troubleshoot Medical Device Issues",
      description: "Contact manufacturer support for CPAP, insulin pump, or other device problems"
    }),
    new Task({
      id: "second-opinion",
      title: "Request Second Opinion Consultation",
      description: "Coordinate with insurance and new provider for additional medical review"
    }),
    new Task({
      id: "genetic-counseling",
      title: "Schedule Genetic Counseling Session",
      description: "Book consultation to discuss genetic test results and implications"
    }),
    new Task({
      id: "medical-transport",
      title: "Schedule Non-Emergency Medical Transport",
      description: "Arrange transportation for routine medical appointments"
    }),
    new Task({
      id: "pharmacy-transfer",
      title: "Transfer Prescriptions to New Pharmacy",
      description: "Coordinate medication transfer between pharmacies"
    }),
    new Task({
      id: "medical-equipment",
      title: "Order Durable Medical Equipment",
      description: "Coordinate with supplier for medical equipment delivery and setup"
    }),
    new Task({
      id: "sleep-study",
      title: "Schedule Sleep Study Assessment",
      description: "Book overnight sleep analysis appointment"
    })
  ],
  "Appointments & Scheduling": [
    new Task({
      id: "haircut",
      title: "Book a Haircut",
      description: "Schedule an appointment at a salon"
    }),
    new Task({
      id: "massage",
      title: "Book Couples Massage",
      description: "Schedule a relaxing spa day"
    }),
    new Task({
      id: "cleaning",
      title: "Schedule Home Cleaning",
      description: "Book a professional cleaning service"
    }),
    new Task({
      id: "tax-pro",
      title: "Schedule Tax Preparation Appointment",
      description: "Coordinate with an accountant or tax pro for filing assistance"
    }),
    new Task({
      id: "wedding-planner",
      title: "Set Up Wedding Planning Consultation",
      description: "Arrange a call to discuss event details, budgets, and timelines"
    }),
    new Task({
      id: "passport-office",
      title: "Book Appointment at Passport Office",
      description: "Secure a spot for new passport application or renewal"
    }),
    new Task({
      id: "consultant-meeting",
      title: "Arrange Business Consultant Meeting",
      description: "Coordinate time with a specialist to discuss growth or strategy"
    }),
    new Task({
      id: "house-rental-viewing",
      title: "Book House Rental Viewing",
      description: "Find a time to tour a rental property or apartment"
    }),
    new Task({
      id: "birthday-party-venue",
      title: "Reserve Birthday Party Venue",
      description: "Lock in event space for a children's or adult birthday party"
    }),
    new Task({
      id: "language-tutor",
      title: "Schedule Language Tutor Session",
      description: "Plan phone or in-person lessons with a tutor"
    }),
    new Task({
      id: "home-renovation-estimate",
      title: "Book Home Renovation Estimate",
      description: "Invite a contractor for a quote on remodeling tasks"
    }),
    new Task({
      id: "pet-grooming-schedule",
      title: "Schedule Pet Grooming",
      description: "Arrange a grooming session for your dog or cat"
    }),
    new Task({
      id: "test-drive-car",
      title: "Book Car Test Drive",
      description: "Reserve a time at the dealership to drive a new model"
    }),
    new Task({
      id: "ems-conference",
      title: "RSVP for Professional Conference",
      description: "Confirm attendance at a business or medical conference"
    }),
    new Task({
      id: "tattoo-consultation",
      title: "Set Up Tattoo Consultation",
      description: "Speak with an artist about design ideas and quotes"
    }),
    new Task({
      id: "career-counselor",
      title: "Schedule Career Counseling",
      description: "Plan call or in-person sessions for job search guidance"
    }),
    new Task({
      id: "music-lesson",
      title: "Book Guitar/Instrument Lessons",
      description: "Coordinate consistent practice slots with an instructor"
    }),
    new Task({
      id: "mobile-mechanic",
      title: "Arrange Mobile Mechanic Visit",
      description: "Schedule car repairs at home or office location"
    }),
    new Task({
      id: "art-exhibit",
      title: "Reserve Tickets for Art Exhibit",
      description: "Set a time to see a limited-run or special art show"
    }),
    new Task({
      id: "dmv-appointment",
      title: "Schedule Appointments with the DMV",
      description: "Book road test or license renewal appointment"
    }),
    new Task({
      id: "notary-appointment",
      title: "Schedule Notary Appointment or Remote Session",
      description: "Confirm location availability, required IDs, or fees"
    }),
    new Task({
      id: "testing-accommodations",
      title: "Submit Special Exam Accommodation Form",
      description: "Confirm with test administrators any needed medical or disability accommodations"
    }),
    new Task({
      id: "government-test",
      title: "Register for Standardized Government Exam",
      description: "Enroll by phone, clarify ID requirements, and confirm location or scheduling"
    })
  ],
  "Customer Service": [
    new Task({
      id: "tech-support-hold",
      title: "Skip Tech Support Hold",
      description: "Let AI wait on hold for you"
    }),
    new Task({
      id: "refund",
      title: "Request Product Refund",
      description: "Get your money back on faulty items"
    }),
    new Task({
      id: "cancel-subscription",
      title: "Cancel Subscription",
      description: "End recurring payments hassle-free"
    }),
    new Task({
      id: "appliance-warranty",
      title: "Check Appliance Warranty Status",
      description: "Call manufacturer to see if repairs are covered"
    }),
    new Task({
      id: "subscription-change",
      title: "Change Subscription Tier",
      description: "Upgrade or downgrade a membership or recurring plan"
    }),
    new Task({
      id: "ups-delivery-delay",
      title: "Investigate Delayed Package Delivery",
      description: "Follow up with the carrier to track a missing shipment"
    }),
    new Task({
      id: "restaurant-complaint",
      title: "Lodge Food Delivery Complaint",
      description: "Inform a restaurant about incorrect or poor-quality orders"
    }),
    new Task({
      id: "booking-error",
      title: "Fix Booking Error (Travel or Event)",
      description: "Straighten out incorrect details in a reservation"
    }),
    new Task({
      id: "on-hold-service",
      title: "Wait on Hold for Busy Customer Service",
      description: "AI waits on hold, then connects you once an agent picks up"
    }),
    new Task({
      id: "internet-outage",
      title: "Report Internet Outage",
      description: "Call ISP to check for outages and estimated repair times",
      additional_metadata: cableMeta
    }),
    new Task({
      id: "gift-card-activation",
      title: "Activate Gift Card",
      description: "Enable funds on gift card or handle replacement if invalid"
    }),
    new Task({
      id: "service-upgrade",
      title: "Upgrade Plan with Current Provider",
      description: "Request better phone, cable, or streaming plans",
      additional_metadata: cableMeta
    }),
    new Task({
      id: "new-member-orientation",
      title: "Set Up Membership Orientation",
      description: "Arrange an introduction call for new members or subscribers"
    }),
    new Task({
      id: "cable-box-reset",
      title: "Troubleshoot Cable Box",
      description: "Walk through a reset or exchange procedure with support",
      additional_metadata: cableMeta
    }),
    new Task({
      id: "loyalty-points-transfer",
      title: "Transfer Loyalty Points",
      description: "Move points to another account or family member"
    }),
    new Task({
      id: "pre-order-query",
      title: "Check Pre-Order Ship Date",
      description: "Confirm shipping estimates on a pre-ordered product"
    }),
    new Task({
      id: "address-change",
      title: "Update Address with Service Provider",
      description: "Inform companies about your new location for shipping and billing"
    }),
    new Task({
      id: "phone-troubleshoot",
      title: "Technical Support for Home Phone Issues",
      description: "Diagnose static, dropped calls, or device malfunctions"
    }),
    new Task({
      id: "client-complaint-followup",
      title: "Client Complaint Follow-Up",
      description: "Businesses can respond to upset clients or customers"
    }),
    new Task({
      id: "airline-compensation",
      title: "Claim Flight Delay Compensation",
      description:
        "Reach out to the airline's customer service to request partial refunds or vouchers for long delays or cancellations"
    }),
    new Task({
      id: "appeal-banned-account",
      title: "Request Account Reactivation for Suspected Violation",
      description:
        "Call a social media or service platform to appeal a ban, present evidence of compliance, or clarify a misunderstanding"
    }),
    new Task({
      id: "fight-corporations",
      title: "File Formal Complaint with Corporate HQ",
      description: "Elevate unresolved issues to a corporate representative for a more favorable solution"
    }),
    new Task({
      id: "fight-spam",
      title: "Report Excessive Spam to Provider",
      description: "Call the email service to block domains or set up advanced spam filters"
    }),
    new Task({
      id: "hotel-complaints",
      title: "Report Poor Stay and Request Partial Refund",
      description: "Reach out to hotel management to seek compensation for mistakes or subpar conditions"
    }),
    new Task({
      id: "hotel-upgrades",
      title: "Request Complimentary Room Upgrade",
      description: "Call prior to arrival to check for available suite upgrades or loyalty perks"
    }),
    new Task({
      id: "jump-phone-queue",
      title: "Queue Up for Busy Support Desk",
      description: "Let the AI dial and wait for hours so the user only joins when a human agent is available"
    }),
    new Task({
      id: "price-match",
      title: "Call Retailer to Match Competitor's Lower Price",
      description: "Provide proof of competitor pricing, request immediate adjustment or refund"
    }),
    new Task({
      id: "warranty-claims",
      title: "File Claim for Defective or Broken Product",
      description: "Call the manufacturer to kick-off a warranty replacement or discuss repair coverage"
    })
  ],
  "Vehicle & Transportation": [
    new Task({
      id: "oil-change",
      title: "Schedule Oil Change",
      description: "Book your next car maintenance"
    }),
    new Task({
      id: "car-registration",
      title: "Renew Car Registration",
      description: "Handle DMV calls and paperwork"
    }),
    new Task({
      id: "towing",
      title: "Request Towing Service",
      description: "Get immediate roadside assistance"
    }),
    new Task({
      id: "mechanic-appointment",
      title: "Book Mechanic Service Appointment",
      description: "Get your car a slot at the auto shop for check-ups or repairs"
    }),
    new Task({
      id: "roadside-assistance",
      title: "Arrange Roadside Assistance",
      description: "Set up tow truck or flat tire help at your location"
    }),
    new Task({
      id: "car-buying",
      title: "Negotiate Car Purchase Price",
      description: "See if the dealership will lower the final out-the-door price"
    }),
    new Task({
      id: "dmv-app",
      title: "Book DMV Appointment",
      description: "Secure a slot for driver's license renewal or test"
    }),
    new Task({
      id: "rideshare-support",
      title: "File Rideshare Complaint",
      description: "Report poor driver behavior or fee discrepancies"
    }),
    new Task({
      id: "bike-shop",
      title: "Schedule Bike Repair",
      description: "Arrange tune-ups, tire fixes, or gear adjustments"
    }),
    new Task({
      id: "flight-rebooking",
      title: "Rebook Flight",
      description: "Change your itinerary or recover from canceled flight"
    }),
    new Task({
      id: "moving-van",
      title: "Rent a Moving Van",
      description: "Get quotes and confirm pickup times for moving vehicles"
    }),
    new Task({
      id: "car-wash",
      title: "Mobile Car Wash Appointment",
      description: "Set up an on-location wash for convenience"
    }),
    new Task({
      id: "uber-account",
      title: "Update Rideshare Payment Info",
      description: "Call to fix or change payment method on your account"
    }),
    new Task({
      id: "tire-change",
      title: "Schedule Tire Change/Rotation",
      description: "Get new tires or rotate them for even wear"
    }),
    new Task({
      id: "car-delivery",
      title: "Corporate Fleet Delivery Coordination",
      description: "Businesses can arrange transport of multiple company cars"
    }),
    new Task({
      id: "public-transit",
      title: "Buy Public Transit Pass",
      description: "Purchase monthly pass or load funds over the phone"
    }),
    new Task({
      id: "rail-refund",
      title: "Request Train Ticket Refund",
      description: "Follow up on canceled or delayed trains for compensation"
    }),
    new Task({
      id: "airport-parking",
      title: "Reserve Airport Parking",
      description: "Book in advance for your next trip"
    }),
    new Task({
      id: "license-rectification",
      title: "Rectify Possible License Suspension",
      description: "Confirm if your license is suspended and next steps to fix"
    }),
    new Task({
      id: "car-lease-negotiation",
      title: "Discuss End-of-Lease Buyout Terms",
      description: "Speak with the leasing provider to explore purchase options or ask for a reduced buyout price"
    }),
    new Task({
      id: "car-repossession",
      title: "Negotiate Repossession Terms with Lender",
      description: "Call the financing company to arrange a payment plan or request a deferral to avoid repossession"
    }),
    new Task({
      id: "rental-car-upgrades",
      title: "Negotiate Upgrade for Rental Reservation",
      description: "Ask if an enhanced vehicle class or features can be added for free or low cost"
    }),
    new Task({
      id: "toll-booth-tickets",
      title: "Challenge Unexpected or Erroneous Toll Charges",
      description:
        "Contact the toll authority to dispute incorrect charges or request fee waivers for delays in payment"
    }),
    new Task({
      id: "vehicle-recall",
      title: "Check and Schedule Vehicle Recall Service",
      description: "Verify recall status and book repair appointment with dealer"
    }),
    new Task({
      id: "emissions-test",
      title: "Schedule Vehicle Emissions Test",
      description: "Book required emissions or smog check appointment"
    }),
    new Task({
      id: "parking-permit",
      title: "Apply for Residential Parking Permit",
      description: "Contact city parking authority for permit application and approval"
    }),
    new Task({
      id: "vehicle-storage",
      title: "Arrange Long-term Vehicle Storage",
      description: "Book secure storage facility for car or RV"
    }),
    new Task({
      id: "vehicle-transport",
      title: "Schedule Vehicle Transport Service",
      description: "Book car shipping for long-distance moves"
    }),
    new Task({
      id: "driving-record",
      title: "Request Official Driving Record",
      description: "Obtain certified copy of driving history from DMV"
    }),
    new Task({
      id: "commercial-license",
      title: "Schedule Commercial License Testing",
      description: "Book CDL written and practical exam appointments"
    })
  ],
  "Personal & Social": [
    new Task({
      id: "voicemail",
      title: "Leave Fun Voicemail",
      description: "Send an AI-powered message"
    }),
    new Task({
      id: "restaurant",
      title: "Check Restaurant Reservation",
      description: "Confirm your dining plans"
    }),
    new Task({
      id: "takeout",
      title: "Order Takeout",
      description: "Place your food order via phone"
    }),
    new Task({
      id: "school-admissions",
      title: "Check School Admission Application Status",
      description: "Follow up on application or documents needed"
    }),
    new Task({
      id: "wedding-invite-rsvp",
      title: "Gather RSVPs for Wedding Invitations",
      description: "Reach out to guests who haven't responded yet"
    }),
    new Task({
      id: "volunteer-event",
      title: "Sign Up for Volunteer Opportunity",
      description: "Coordinate shift times or details for charity events"
    }),
    new Task({
      id: "house-sitter",
      title: "Arrange House Sitter",
      description: "Coordinate or interview someone to watch over your home"
    }),
    new Task({
      id: "reunion-planning",
      title: "Plan a Family or Class Reunion",
      description: "Contact event spaces or caterers for quotes and availability"
    }),
    new Task({
      id: "florist-order",
      title: "Coordinate Flower Delivery",
      description: "Schedule deliveries for special events or condolences"
    }),
    new Task({
      id: "chef-reservation",
      title: "Hire a Personal Chef",
      description: "Discuss menus, pricing, and schedule with a local chef"
    }),
    new Task({
      id: "language-exchange",
      title: "Find a Language Exchange Partner",
      description: "Arrange phone calls or meet-ups for practicing a new language"
    }),
    new Task({
      id: "lost-phone",
      title: "Report Lost or Stolen Phone",
      description: "Contact the carrier to lock the device and prevent unauthorized use"
    }),
    new Task({
      id: "community-meeting",
      title: "Attend Community Board Meeting",
      description: "RSVP with neighborhood committees or HOAs"
    }),
    new Task({
      id: "library-hold",
      title: "Place a Library Book on Hold",
      description: "Reserve popular or new releases before they're checked out"
    }),
    new Task({
      id: "apartment-renewal",
      title: "Discuss Apartment Lease Renewal",
      description: "Negotiate lease terms with landlord or property manager"
    }),
    new Task({
      id: "family-visit",
      title: "Coordinate Family Visit Plans",
      description: "Confirm arrival times, lodging, or local activities"
    }),
    new Task({
      id: "dog-walker",
      title: "Hire a Dog Walker",
      description: "Arrange daily or weekly walks for busy schedules"
    }),
    new Task({
      id: "tutor-subject",
      title: "Find a Subject-Specific Tutor",
      description: "Look for a teacher in math, science, or language arts"
    }),
    new Task({
      id: "fitness-class-rsvp",
      title: "RSVP for a Local Fitness Class",
      description: "Sign up for yoga, spinning, or group workouts"
    }),
    new Task({
      id: "animal-control",
      title: "Report Stray or Wild Animal",
      description: "Contact local animal control to report stray or potentially dangerous wildlife in the neighborhood"
    }),
    new Task({
      id: "birth-certificates",
      title: "Request Official Birth Certificate Copy",
      description: "Call the local vital records office to order an official copy or expedite shipping"
    }),
    new Task({
      id: "marriage-certificates",
      title: "Obtain Official Marriage Certificate Copy",
      description: "Follow up with the vital records office if the document is delayed or incomplete"
    }),
    new Task({
      id: "lost-pet",
      title: "Alert Shelters and Local Vets About Missing Pet",
      description: "Provide microchip info or relevant details to accelerate the search for a lost animal"
    }),
    new Task({
      id: "voter-registration-confirmation",
      title: "Confirm or Update Voter Registration Details",
      description: "Provide relevant ID or address information to finalize the registration"
    })
  ],
  "Business & Work": [
    new Task({
      id: "customer-feedback-followup",
      title: "Collect Customer Feedback for Product",
      description: "Call past customers for product improvement insights"
    }),
    new Task({
      id: "vendor-invoice",
      title: "Confirm Vendor Invoice Payment Date",
      description: "Check when a business partner expects payment for services"
    }),
    new Task({
      id: "team-scheduling",
      title: "Schedule Team Check-In Call",
      description: "Arrange a group meeting to review project progress"
    }),
    new Task({
      id: "cold-call-prospect",
      title: "Cold Call Potential Sales Prospect",
      description: "Reach out to leads about a new product or service"
    }),
    new Task({
      id: "copyright-protection",
      title: "File Complaint for Unauthorized Use of Content",
      description: "Call a hosting platform or an infringer's organization to demand takedown or licensing fees"
    }),
    new Task({
      id: "delaware-220",
      title: "Request Corporate Records from Delaware Corporation",
      description: "Coordinate phone calls to compel the release of essential documents under Delaware law"
    }),
    new Task({
      id: "parental-leave",
      title: "Confirm Parental Leave Policy with HR",
      description: "Clarify the length of leave, whether it's paid, and any forms needed"
    }),
    new Task({
      id: "negotiate-salary",
      title: "Contact HR to Discuss Compensation Package",
      description: "Present achievements or market data to justify a raise or bonus"
    })
  ],
  "Legal & Compliance": [
    new Task({
      id: "court-date-change",
      title: "Schedule Court Date Changes",
      description: "Modify your court appearance date"
    }),
    new Task({
      id: "public-defender",
      title: "Request Public Defender Assignment",
      description: "Get legal representation assigned to your case"
    }),
    new Task({
      id: "small-claims-docs",
      title: "File Small Claims Documentation",
      description: "Submit and process small claims court paperwork"
    }),
    new Task({
      id: "court-records",
      title: "Request Court Record Copies",
      description: "Obtain official copies of court proceedings"
    }),
    new Task({
      id: "probation-check",
      title: "Check Probation Requirements",
      description: "Verify current probation status and obligations"
    }),
    new Task({
      id: "license-verification",
      title: "Verify Professional License Status",
      description: "Check status of professional certifications"
    }),
    new Task({
      id: "legal-aid-request",
      title: "Request Legal Aid Services",
      description: "Connect with free or low-cost legal assistance"
    }),
    new Task({
      id: "immigration-appointment",
      title: "Schedule Immigration Appointments",
      description: "Book meetings with immigration services"
    }),
    new Task({
      id: "visa-status",
      title: "Check Visa Application Status",
      description: "Get updates on pending visa applications"
    })
  ],
  "Education & Training": [
    new Task({
      id: "transfer-records",
      title: "Request School Transfer Records",
      description: "Coordinate transfer of academic records"
    }),
    new Task({
      id: "parent-teacher",
      title: "Schedule Parent-Teacher Conferences",
      description: "Book meetings with teachers and staff"
    }),
    new Task({
      id: "loan-deferment",
      title: "Apply for Student Loan Deferment",
      description: "Request temporary pause on loan payments"
    }),
    new Task({
      id: "campus-tour",
      title: "Schedule College Campus Tours",
      description: "Book guided tours of educational institutions"
    }),
    new Task({
      id: "grade-appeal",
      title: "Request Grade Appeals",
      description: "Challenge and review academic grades"
    })
  ],
  "Real Estate & Property": [
    new Task({
      id: "property-inspection",
      title: "Schedule Property Inspections",
      description: "Book professional property evaluations"
    }),
    new Task({
      id: "rental-history",
      title: "Request Rental History Reports",
      description: "Obtain tenant payment and occupancy records"
    }),
    new Task({
      id: "noise-complaint",
      title: "File Noise Complaints",
      description: "Report disruptive noise issues"
    }),
    new Task({
      id: "pest-control",
      title: "Request Pest Control Services",
      description: "Schedule pest treatment and prevention"
    })
  ],
  "Insurance & Claims": [
    new Task({
      id: "weather-damage",
      title: "File Weather Damage Claims",
      description: "Report storm or natural disaster damage"
    }),
    new Task({
      id: "adjuster-visit",
      title: "Schedule Insurance Adjuster Visits",
      description: "Book property damage assessment"
    }),
    new Task({
      id: "policy-docs",
      title: "Request Policy Documentation",
      description: "Obtain copies of insurance policies"
    }),
    new Task({
      id: "claim-appeal",
      title: "Appeal Claim Denials",
      description: "Challenge rejected insurance claims"
    })
  ],
  "Senior Services": [
    new Task({
      id: "medicare-consult",
      title: "Schedule Medicare Consultations",
      description: "Get help with Medicare options and enrollment"
    }),
    new Task({
      id: "senior-transport-request",
      title: "Request Senior Transportation",
      description: "Book rides for medical appointments"
    }),
    new Task({
      id: "meal-delivery",
      title: "Coordinate Meal Delivery Services",
      description: "Set up regular meal delivery"
    }),
    new Task({
      id: "health-assessment",
      title: "Schedule Home Health Assessments",
      description: "Book in-home care evaluations"
    })
  ],
  "Emergency & Crisis Services": [
    new Task({
      id: "power-outage",
      title: "Report Power Outages",
      description: "Report and check status of power failures"
    }),
    new Task({
      id: "emergency-housing",
      title: "Request Emergency Housing",
      description: "Find immediate temporary housing"
    }),
    new Task({
      id: "crisis-counseling",
      title: "Schedule Crisis Counseling",
      description: "Get immediate mental health support"
    }),
    new Task({
      id: "emergency-repairs-schedule",
      title: "Schedule Emergency Home Repairs",
      description: "Book urgent home repair services"
    }),
    new Task({
      id: "disaster-assistance",
      title: "Apply for Disaster Relief",
      description: "Contact FEMA or relief organizations for emergency aid"
    }),
    new Task({
      id: "emergency-shelter",
      title: "Find Emergency Housing",
      description: "Locate immediate temporary shelter options"
    }),
    new Task({
      id: "crisis-hotline",
      title: "Connect with Crisis Counselor",
      description: "Get immediate mental health support"
    }),
    new Task({
      id: "utility-emergency",
      title: "Report Utility Emergency",
      description: "Report gas leaks or dangerous electrical issues"
    }),
    new Task({
      id: "poison-control",
      title: "Contact Poison Control",
      description: "Get immediate guidance for exposure to harmful substances"
    }),
    new Task({
      id: "domestic-violence",
      title: "Access Domestic Violence Support",
      description: "Connect with safe housing and support services"
    }),
    new Task({
      id: "emergency-repairs-schedule-home",
      title: "Schedule Emergency Home Repair",
      description: "Get immediate assistance for critical home issues"
    }),
    new Task({
      id: "weather-emergency",
      title: "Report Weather-Related Emergency",
      description: "Report immediate dangers from severe weather"
    })
  ],
  "Technology & Digital Services": [
    new Task({
      id: "account-reset",
      title: "Reset Online Account Access",
      description: "Recover locked or forgotten accounts"
    }),
    new Task({
      id: "tech-support-schedule",
      title: "Schedule Tech Support Calls",
      description: "Book technical assistance appointments"
    }),
    new Task({
      id: "data-recovery",
      title: "Request Data Recovery Services",
      description: "Recover lost or corrupted data"
    }),
    new Task({
      id: "smart-home",
      title: "Schedule Smart Home Installation",
      description: "Book smart device setup services"
    })
  ],
  "Travel & Tourism": [
    new Task({
      id: "lost-baggage",
      title: "Request Lost Baggage Search",
      description: "Track and recover missing luggage"
    }),
    new Task({
      id: "airport-shuttle",
      title: "Schedule Airport Shuttle Service",
      description: "Book reliable airport transportation"
    }),
    new Task({
      id: "travel-insurance",
      title: "Request Travel Insurance Claims",
      description: "File claims for trip cancellations or delays"
    }),
    new Task({
      id: "tour-guide",
      title: "Schedule Tour Guide Services",
      description: "Book professional tour guides"
    })
  ],
  "Travel & Tourism Services": [
    new Task({
      id: "flight-compensation",
      title: "Claim Flight Delay Compensation",
      description: "File claim for delayed or cancelled flights"
    }),
    new Task({
      id: "hotel-price-match",
      title: "Request Hotel Price Match",
      description: "Contact hotel to match competitor's lower rate"
    }),
    new Task({
      id: "travel-insurance-claim",
      title: "File Travel Insurance Claim",
      description: "Submit claim for trip cancellation or interruption"
    }),
    new Task({
      id: "points-transfer",
      title: "Transfer Travel Reward Points",
      description: "Move points between loyalty programs"
    }),
    new Task({
      id: "visa-assistance",
      title: "Request Travel Visa Assistance",
      description: "Get help with visa application process"
    }),
    new Task({
      id: "airport-lounge",
      title: "Book Airport Lounge Access",
      description: "Reserve space in airport lounges"
    }),
    new Task({
      id: "cruise-upgrade",
      title: "Request Cruise Cabin Upgrade",
      description: "Negotiate for better cabin assignment"
    }),
    new Task({
      id: "travel-medical",
      title: "Arrange Travel Medical Insurance",
      description: "Set up medical coverage for international travel"
    })
  ],
  "Entertainment & Events": [
    new Task({
      id: "event-photography",
      title: "Schedule Event Photography",
      description: "Book professional event photographers"
    }),
    new Task({
      id: "venue-tour",
      title: "Request Venue Tours",
      description: "Schedule venue visits for events"
    }),
    new Task({
      id: "entertainment-booking",
      title: "Schedule Entertainment Booking",
      description: "Reserve performers for events"
    }),
    new Task({
      id: "event-insurance",
      title: "Request Event Insurance Quotes",
      description: "Get coverage for special events"
    })
  ],
  "Professional Services": [
    new Task({
      id: "tax-prep",
      title: "Schedule Tax Preparation",
      description: "Book appointments with tax professionals"
    }),
    new Task({
      id: "business-license",
      title: "Request Business License Applications",
      description: "Process business licensing paperwork"
    }),
    new Task({
      id: "legal-consult",
      title: "Schedule Legal Consultations",
      description: "Book meetings with attorneys"
    }),
    new Task({
      id: "accounting-services",
      title: "Request Accounting Services",
      description: "Book professional accounting help"
    })
  ],
  "Government & Civic Services": [
    new Task({
      id: "jury-duty",
      title: "Schedule Jury Duty Deferrals",
      description: "Request postponement of jury service"
    }),
    new Task({
      id: "building-permit",
      title: "Request Building Permits",
      description: "Apply for construction permits"
    }),
    new Task({
      id: "food-stamp",
      title: "Schedule Food Stamp Interviews",
      description: "Book required benefit interviews"
    }),
    new Task({
      id: "public-records",
      title: "Request Public Records",
      description: "Access government documents"
    })
  ],
  "Employment & Career": [
    new Task({
      id: "job-interview",
      title: "Schedule Job Interviews",
      description: "Coordinate interview times"
    }),
    new Task({
      id: "employment-verify",
      title: "Request Employment Verification",
      description: "Obtain work history documentation"
    }),
    new Task({
      id: "workers-comp",
      title: "File Workers Compensation Claims",
      description: "Report workplace injuries"
    }),
    new Task({
      id: "performance-review",
      title: "Schedule Performance Reviews",
      description: "Book employee evaluations"
    })
  ],
  "Home Services & Utilities": [
    new Task({
      id: "hvac-maintenance",
      title: "Schedule HVAC Maintenance",
      description: "Book seasonal heating and cooling system check-up"
    }),
    new Task({
      id: "plumbing-emergency",
      title: "Request Emergency Plumber",
      description: "Get immediate assistance for water leaks or blockages"
    }),
    new Task({
      id: "solar-consultation",
      title: "Book Solar Panel Consultation",
      description: "Schedule assessment for home solar installation"
    }),
    new Task({
      id: "security-system",
      title: "Set Up Home Security Installation",
      description: "Schedule professional security system setup"
    }),
    new Task({
      id: "lawn-service",
      title: "Schedule Lawn Care Service",
      description: "Set up regular or one-time lawn maintenance"
    }),
    new Task({
      id: "chimney-sweep",
      title: "Book Chimney Cleaning",
      description: "Schedule annual chimney inspection and cleaning"
    }),
    new Task({
      id: "window-repair",
      title: "Arrange Window Repair Service",
      description: "Book appointment for window replacement or repair"
    }),
    new Task({
      id: "trash-service",
      title: "Set Up Waste Collection Service",
      description: "Arrange residential garbage pickup schedule"
    })
  ],
  "Immigration & International Services": [
    new Task({
      id: "visa-extension",
      title: "Request Visa Extension",
      description: "Contact immigration office about extending current visa status"
    }),
    new Task({
      id: "passport-renewal",
      title: "Schedule Passport Renewal Appointment",
      description: "Book appointment for expedited passport processing"
    }),
    new Task({
      id: "immigration-lawyer",
      title: "Schedule Immigration Attorney Consultation",
      description: "Book initial meeting with immigration legal expert"
    }),
    new Task({
      id: "embassy-appointment",
      title: "Schedule Embassy Appointment",
      description: "Book time slot for document processing or interview"
    }),
    new Task({
      id: "citizenship-class",
      title: "Register for Citizenship Classes",
      description: "Sign up for naturalization test preparation courses"
    }),
    new Task({
      id: "document-translation",
      title: "Request Official Document Translation",
      description: "Arrange certified translation of important documents"
    }),
    new Task({
      id: "foreign-credential",
      title: "Verify Foreign Credentials",
      description: "Contact evaluation services for degree/certificate verification"
    })
  ],
  "Professional Development & Career Services": [
    new Task({
      id: "certification-exam",
      title: "Schedule Professional Certification Exam",
      description: "Book testing appointment for industry certifications"
    }),
    new Task({
      id: "resume-review",
      title: "Book Resume Review Session",
      description: "Schedule professional resume critique and feedback"
    }),
    new Task({
      id: "career-coach",
      title: "Schedule Career Coach Meeting",
      description: "Book session with career development specialist"
    }),
    new Task({
      id: "skill-assessment",
      title: "Schedule Skills Assessment Test",
      description: "Book professional competency evaluation"
    }),
    new Task({
      id: "networking-event",
      title: "Register for Professional Networking Event",
      description: "Sign up for industry meetups or conferences"
    }),
    new Task({
      id: "linkedin-optimization",
      title: "Schedule LinkedIn Profile Review",
      description: "Book consultation for social media presence optimization"
    }),
    new Task({
      id: "mock-interview",
      title: "Schedule Mock Interview Practice",
      description: "Book practice interview with feedback session"
    })
  ],
  "Pet Services & Veterinary Care": [
    new Task({
      id: "pet-vaccination",
      title: "Schedule Pet Vaccination Update",
      description: "Book appointment for routine pet immunizations"
    }),
    new Task({
      id: "pet-boarding",
      title: "Reserve Pet Boarding Services",
      description: "Book kennel or pet hotel for vacation care"
    }),
    new Task({
      id: "pet-dental",
      title: "Schedule Pet Dental Cleaning",
      description: "Book professional dental care for your pet"
    }),
    new Task({
      id: "pet-grooming-book",
      title: "Book Pet Grooming Session",
      description: "Schedule professional grooming appointment"
    }),
    new Task({
      id: "emergency-vet",
      title: "Locate 24-Hour Emergency Vet",
      description: "Find and contact nearest emergency animal hospital"
    }),
    new Task({
      id: "pet-insurance-claim",
      title: "File Pet Insurance Claim",
      description: "Submit and track pet medical insurance claims"
    }),
    new Task({
      id: "pet-microchip",
      title: "Update Pet Microchip Information",
      description: "Contact registry to update contact information"
    }),
    new Task({
      id: "pet-prescription",
      title: "Refill Pet Medication",
      description: "Order prescription refills from vet pharmacy"
    })
  ],
  "Consumer Protection & Advocacy": [
    new Task({
      id: "robocall-complaint",
      title: "File Robocall Complaint",
      description: "Report unwanted automated calls to FTC"
    }),
    new Task({
      id: "price-gouging",
      title: "Report Price Gouging",
      description: "File complaint about excessive price increases"
    }),
    new Task({
      id: "identity-theft",
      title: "Report Identity Theft",
      description: "Contact credit bureaus and file official reports"
    }),
    new Task({
      id: "scam-report",
      title: "Report Consumer Scam",
      description: "File complaint about fraudulent business practices"
    }),
    new Task({
      id: "warranty-dispute",
      title: "Dispute Warranty Denial",
      description: "Challenge rejected warranty claims"
    }),
    new Task({
      id: "data-breach",
      title: "Report Data Privacy Breach",
      description: "Notify authorities about personal data exposure"
    }),
    new Task({
      id: "debt-collector",
      title: "Challenge Debt Collector Practices",
      description: "Report violations of debt collection regulations"
    }),
    new Task({
      id: "consumer-rights",
      title: "Request Consumer Rights Information",
      description: "Get guidance on consumer protection laws"
    })
  ],
  "Digital Services & Online Accounts": [
    new Task({
      id: "account-recovery",
      title: "Recover Hacked Account",
      description: "Get help restoring access to compromised accounts"
    }),
    new Task({
      id: "subscription-cancel",
      title: "Cancel Digital Subscription",
      description: "End recurring payments for online services"
    }),
    new Task({
      id: "streaming-dispute",
      title: "Dispute Streaming Service Charges",
      description: "Challenge unauthorized streaming service fees"
    }),
    new Task({
      id: "social-impersonation",
      title: "Report Social Media Impersonation",
      description: "File complaint about fake profiles using your identity"
    }),
    new Task({
      id: "cloud-storage",
      title: "Resolve Cloud Storage Issues",
      description: "Address problems with online storage services"
    }),
    new Task({
      id: "domain-support",
      title: "Contact Domain Registrar Support",
      description: "Get help with domain registration issues"
    }),
    new Task({
      id: "gaming-account",
      title: "Recover Gaming Account",
      description: "Restore access to locked gaming accounts"
    }),
    new Task({
      id: "digital-purchase",
      title: "Dispute Digital Purchase",
      description: "Challenge charges for digital goods or services"
    })
  ],
  "Educational Support & Academic Services": [
    new Task({
      id: "scholarship-application",
      title: "Submit Scholarship Application",
      description: "Apply for educational funding opportunities"
    }),
    new Task({
      id: "transcript-request",
      title: "Request Academic Transcripts",
      description: "Order official school records and transcripts"
    }),
    new Task({
      id: "enrollment-verification",
      title: "Verify School Enrollment Status",
      description: "Confirm current academic enrollment status"
    }),
    new Task({
      id: "testing-accommodation",
      title: "Request Testing Accommodations",
      description: "Arrange special testing conditions for disabilities"
    }),
    new Task({
      id: "degree-verification",
      title: "Verify Academic Credentials",
      description: "Confirm degree completion and authenticity"
    }),
    new Task({
      id: "course-registration",
      title: "Register for Academic Courses",
      description: "Enroll in classes for upcoming term"
    }),
    new Task({
      id: "academic-appeal",
      title: "File Academic Appeal",
      description: "Challenge academic decisions or grades"
    }),
    new Task({
      id: "student-housing",
      title: "Apply for Student Housing",
      description: "Submit application for campus housing"
    })
  ],
  "Government & Public Services": [
    new Task({
      id: "foia-request",
      title: "Submit FOIA Request",
      description: "File Freedom of Information Act document request"
    }),
    new Task({
      id: "voter-registration-update",
      title: "Update Voter Registration",
      description: "Modify or confirm voter registration status"
    }),
    new Task({
      id: "benefits-verification",
      title: "Verify Government Benefits Status",
      description: "Check status of public assistance benefits"
    }),
    new Task({
      id: "public-housing",
      title: "Apply for Public Housing",
      description: "Submit application for housing assistance"
    }),
    new Task({
      id: "social-security",
      title: "Check Social Security Benefits",
      description: "Verify social security payment status"
    }),
    new Task({
      id: "vital-records",
      title: "Order Vital Records",
      description: "Request birth, death, or marriage certificates"
    }),
    new Task({
      id: "disability-claim",
      title: "File Disability Claim",
      description: "Submit application for disability benefits"
    }),
    new Task({
      id: "veterans-benefits",
      title: "Check Veterans Benefits Status",
      description: "Verify VA benefits and claims status"
    })
  ],
  "Legal & Documentation Services": [
    new Task({
      id: "notary-scheduling",
      title: "Schedule Notary Services",
      description: "Book appointment with mobile notary"
    }),
    new Task({
      id: "legal-document",
      title: "Request Legal Document Copies",
      description: "Obtain copies of court filings or legal records"
    }),
    new Task({
      id: "background-check",
      title: "Request Background Check",
      description: "Process official background verification"
    }),
    new Task({
      id: "small-claims-case",
      title: "File Small Claims Case",
      description: "Submit small claims court documentation"
    }),
    new Task({
      id: "legal-aid-application",
      title: "Apply for Legal Aid",
      description: "Request free or reduced-cost legal assistance"
    }),
    new Task({
      id: "name-change",
      title: "Process Name Change Request",
      description: "File legal name change documentation"
    }),
    new Task({
      id: "power-attorney",
      title: "File Power of Attorney",
      description: "Submit power of attorney documentation"
    }),
    new Task({
      id: "record-expungement",
      title: "Request Record Expungement",
      description: "Apply for criminal record clearing"
    })
  ],
  "Financial Planning & Investment Services": [
    new Task({
      id: "financial-advisor",
      title: "Schedule Financial Planning Session",
      description: "Book consultation with certified financial planner"
    }),
    new Task({
      id: "investment-rebalance",
      title: "Request Portfolio Rebalancing",
      description: "Contact advisor to adjust investment allocations"
    }),
    new Task({
      id: "retirement-planning",
      title: "Book Retirement Planning Session",
      description: "Schedule retirement strategy consultation"
    }),
    new Task({
      id: "estate-planning",
      title: "Schedule Estate Planning Meeting",
      description: "Book consultation for will and trust planning"
    }),
    new Task({
      id: "tax-strategy",
      title: "Book Tax Strategy Consultation",
      description: "Plan tax optimization with financial advisor"
    }),
    new Task({
      id: "college-savings",
      title: "Set Up College Savings Plan",
      description: "Establish 529 or education savings account"
    }),
    new Task({
      id: "debt-consolidation",
      title: "Explore Debt Consolidation Options",
      description: "Discuss loan consolidation with financial advisor"
    }),
    new Task({
      id: "investment-fraud-report",
      title: "Report Investment Fraud",
      description: "Contact regulators about suspicious investment activities"
    })
  ],
  "Religious & Community Services": [
    new Task({
      id: "religious-counseling",
      title: "Schedule Religious Counseling",
      description: "Book appointment with religious leader or counselor"
    }),
    new Task({
      id: "community-volunteer",
      title: "Register for Volunteer Opportunities",
      description: "Sign up for local community service projects"
    }),
    new Task({
      id: "food-bank",
      title: "Access Food Bank Services",
      description: "Connect with local food assistance programs"
    }),
    new Task({
      id: "senior-services",
      title: "Schedule Senior Support Services",
      description: "Arrange meals on wheels or senior assistance"
    }),
    new Task({
      id: "youth-programs",
      title: "Register for Youth Programs",
      description: "Enroll in community youth activities"
    }),
    new Task({
      id: "community-classes",
      title: "Register for Community Classes",
      description: "Sign up for local educational programs"
    }),
    new Task({
      id: "donation-pickup",
      title: "Schedule Donation Pickup",
      description: "Arrange collection of charitable donations"
    }),
    new Task({
      id: "community-event",
      title: "Register for Community Event",
      description: "Sign up for local community gatherings"
    })
  ],
  "Sports & Recreation Services": [
    new Task({
      id: "sports-registration",
      title: "Register for Sports League",
      description: "Sign up for recreational sports teams"
    }),
    new Task({
      id: "fitness-membership",
      title: "Set Up Gym Membership",
      description: "Arrange fitness center enrollment"
    }),
    new Task({
      id: "personal-trainer",
      title: "Schedule Personal Training",
      description: "Book sessions with fitness trainer"
    }),
    new Task({
      id: "sports-equipment",
      title: "Reserve Sports Equipment",
      description: "Book rental of sports gear and equipment"
    }),
    new Task({
      id: "recreation-permits",
      title: "Obtain Recreation Permits",
      description: "Apply for camping or park use permits"
    }),
    new Task({
      id: "sports-facility-book",
      title: "Book Sports Facility",
      description: "Reserve courts, fields, or practice spaces"
    }),
    new Task({
      id: "sports-instruction",
      title: "Schedule Sports Lessons",
      description: "Book individual or group sports instruction"
    }),
    new Task({
      id: "tournament-registration",
      title: "Register for Tournament",
      description: "Sign up for sports competition or event"
    })
  ],
  "Business Support Services": [
    new Task({
      id: "business-registration",
      title: "Register New Business",
      description: "File business registration documents"
    }),
    new Task({
      id: "tax-id",
      title: "Apply for Tax ID Number",
      description: "Obtain federal or state tax identification"
    }),
    new Task({
      id: "business-insurance",
      title: "Set Up Business Insurance",
      description: "Arrange commercial insurance coverage"
    }),
    new Task({
      id: "merchant-services",
      title: "Set Up Payment Processing",
      description: "Establish merchant account for payments"
    }),
    new Task({
      id: "business-permits",
      title: "Obtain Business Permits",
      description: "Apply for required business licenses"
    }),
    new Task({
      id: "payroll-setup",
      title: "Set Up Payroll Services",
      description: "Establish employee payment system"
    }),
    new Task({
      id: "business-banking",
      title: "Open Business Account",
      description: "Set up commercial banking services"
    }),
    new Task({
      id: "vendor-registration",
      title: "Register as Vendor",
      description: "Complete vendor registration process"
    })
  ],
  "Privacy & Data Protection Services": [
    new Task({
      id: "credit-freeze",
      title: "Request Credit Freeze",
      description: "Place security freeze on credit reports"
    }),
    new Task({
      id: "data-removal",
      title: "Request Data Removal",
      description: "Contact companies to delete personal information"
    }),
    new Task({
      id: "privacy-breach",
      title: "Report Privacy Violation",
      description: "File complaint about data privacy breach"
    }),
    new Task({
      id: "opt-out-services",
      title: "Opt Out of Data Collection",
      description: "Remove information from data broker services"
    }),
    new Task({
      id: "identity-monitoring",
      title: "Set Up Identity Monitoring",
      description: "Establish identity theft protection service"
    }),
    new Task({
      id: "donotcall-registry",
      title: "Register for Do Not Call List",
      description: "Add number to telemarketing block list"
    }),
    new Task({
      id: "mail-preferences",
      title: "Update Mail Preferences",
      description: "Opt out of unwanted commercial mail"
    }),
    new Task({
      id: "privacy-audit",
      title: "Request Privacy Audit",
      description: "Review and protect online presence"
    })
  ],
  "Military & Veterans Services": [
    new Task({
      id: "va-healthcare",
      title: "Schedule VA Healthcare Appointment",
      description: "Book medical services at VA facilities"
    }),
    new Task({
      id: "military-records",
      title: "Request Military Service Records",
      description: "Obtain copies of DD-214 or service documentation"
    }),
    new Task({
      id: "gi-bill",
      title: "Verify GI Bill Benefits",
      description: "Check education benefit status and eligibility"
    }),
    new Task({
      id: "va-loan",
      title: "Apply for VA Home Loan",
      description: "Start VA mortgage application process"
    }),
    new Task({
      id: "veteran-id",
      title: "Request Veteran ID Card",
      description: "Apply for official veteran identification"
    }),
    new Task({
      id: "combat-pay",
      title: "Verify Combat Pay Status",
      description: "Check special pay and allowance status"
    }),
    new Task({
      id: "deployment-services",
      title: "Arrange Deployment Services",
      description: "Set up support services for deployment period"
    }),
    new Task({
      id: "veteran-pension",
      title: "Check Veteran Pension Status",
      description: "Verify pension payments and eligibility"
    })
  ],
  "Disability Support Services": [
    new Task({
      id: "disability-equipment",
      title: "Order Adaptive Equipment",
      description: "Arrange delivery of disability support devices"
    }),
    new Task({
      id: "disability-transport",
      title: "Schedule Accessible Transportation",
      description: "Book wheelchair-accessible transport services"
    }),
    new Task({
      id: "disability-benefits",
      title: "Check Disability Benefits Status",
      description: "Verify disability payment status and eligibility"
    }),
    new Task({
      id: "workplace-accommodation",
      title: "Request Workplace Accommodations",
      description: "Arrange necessary workplace modifications"
    }),
    new Task({
      id: "disability-housing",
      title: "Find Accessible Housing",
      description: "Locate disability-friendly housing options"
    }),
    new Task({
      id: "caregiver-services",
      title: "Arrange Caregiver Services",
      description: "Schedule in-home care assistance"
    }),
    new Task({
      id: "disability-advocacy",
      title: "Connect with Disability Advocate",
      description: "Get support for disability rights issues"
    }),
    new Task({
      id: "assistive-technology",
      title: "Set Up Assistive Technology",
      description: "Schedule installation of adaptive devices"
    })
  ],
  "Environmental & Conservation Services": [
    new Task({
      id: "recycling-pickup",
      title: "Schedule Special Recycling Pickup",
      description: "Arrange collection of large or hazardous items"
    }),
    new Task({
      id: "environmental-complaint",
      title: "Report Environmental Violation",
      description: "File complaint about pollution or dumping"
    }),
    new Task({
      id: "energy-audit",
      title: "Schedule Home Energy Audit",
      description: "Book professional energy efficiency assessment"
    }),
    new Task({
      id: "water-quality",
      title: "Report Water Quality Issues",
      description: "File complaint about water contamination"
    }),
    new Task({
      id: "wildlife-incident",
      title: "Report Wildlife Incident",
      description: "Contact authorities about wildlife issues"
    }),
    new Task({
      id: "conservation-volunteer",
      title: "Register for Conservation Project",
      description: "Sign up for environmental volunteer work"
    }),
    new Task({
      id: "tree-service",
      title: "Schedule Tree Inspection",
      description: "Book arborist for tree health assessment"
    }),
    new Task({
      id: "green-certification",
      title: "Apply for Green Certification",
      description: "Start environmental certification process"
    })
  ],
  "Cultural & Arts Services": [
    new Task({
      id: "art-class",
      title: "Register for Art Classes",
      description: "Sign up for visual arts instruction"
    }),
    new Task({
      id: "performance-tickets",
      title: "Book Performance Tickets",
      description: "Reserve seats for cultural performances"
    }),
    new Task({
      id: "gallery-tour",
      title: "Schedule Gallery Tour",
      description: "Book guided art gallery visits"
    }),
    new Task({
      id: "cultural-workshop",
      title: "Register for Cultural Workshop",
      description: "Sign up for cultural education programs"
    }),
    new Task({
      id: "art-submission",
      title: "Submit Art for Exhibition",
      description: "Apply for art show or gallery display"
    }),
    new Task({
      id: "music-lessons",
      title: "Schedule Music Lessons",
      description: "Book instrumental or vocal instruction"
    }),
    new Task({
      id: "cultural-membership",
      title: "Join Cultural Organization",
      description: "Sign up for arts organization membership"
    }),
    new Task({
      id: "art-appraisal",
      title: "Schedule Art Appraisal",
      description: "Book professional artwork valuation"
    })
  ],
  "Transportation & Logistics Services": [
    new Task({
      id: "freight-shipping",
      title: "Schedule Freight Shipment",
      description: "Arrange commercial cargo transportation"
    }),
    new Task({
      id: "courier-pickup",
      title: "Schedule Courier Pickup",
      description: "Arrange express package collection"
    }),
    new Task({
      id: "cargo-tracking",
      title: "Track Lost Cargo",
      description: "Locate missing commercial shipments"
    }),
    new Task({
      id: "customs-clearance",
      title: "Arrange Customs Clearance",
      description: "Process international shipping documentation"
    }),
    new Task({
      id: "delivery-reroute",
      title: "Reroute Package Delivery",
      description: "Change delivery address for shipment"
    }),
    new Task({
      id: "logistics-quote",
      title: "Get Logistics Quote",
      description: "Request shipping cost estimate"
    }),
    new Task({
      id: "warehouse-storage",
      title: "Book Warehouse Storage",
      description: "Reserve commercial storage space"
    }),
    new Task({
      id: "shipping-insurance",
      title: "Purchase Shipping Insurance",
      description: "Arrange cargo insurance coverage"
    })
  ],
  "Senior Care & Aging Services": [
    new Task({
      id: "medicare-enrollment",
      title: "Process Medicare Enrollment",
      description: "Handle Medicare registration and plan selection"
    }),
    new Task({
      id: "senior-transport-schedule",
      title: "Schedule Senior Transportation",
      description: "Book medical transport or senior shuttle services"
    }),
    new Task({
      id: "meals-on-wheels",
      title: "Arrange Meal Delivery Service",
      description: "Set up regular meal delivery for seniors"
    }),
    new Task({
      id: "nursing-home-tour",
      title: "Schedule Nursing Home Tour",
      description: "Book visits to assisted living facilities"
    }),
    new Task({
      id: "senior-benefits",
      title: "Check Senior Benefit Eligibility",
      description: "Verify qualification for senior assistance programs"
    }),
    new Task({
      id: "medical-alert",
      title: "Set Up Medical Alert System",
      description: "Install emergency response system for seniors"
    }),
    new Task({
      id: "elder-care-assessment",
      title: "Schedule Care Assessment",
      description: "Book professional evaluation of care needs"
    }),
    new Task({
      id: "senior-social",
      title: "Register for Senior Activities",
      description: "Sign up for community senior programs"
    })
  ],
  "Child Care & Family Services": [
    new Task({
      id: "daycare-enrollment",
      title: "Process Daycare Enrollment",
      description: "Register child for daycare services"
    }),
    new Task({
      id: "after-school",
      title: "Register for After-School Program",
      description: "Enroll in after-school activities or care"
    }),
    new Task({
      id: "child-care-subsidy",
      title: "Apply for Childcare Assistance",
      description: "Check eligibility for childcare financial aid"
    }),
    new Task({
      id: "summer-camp",
      title: "Register for Summer Camp",
      description: "Book summer program enrollment"
    }),
    new Task({
      id: "tutoring-services",
      title: "Schedule Tutoring Sessions",
      description: "Book academic support appointments"
    }),
    new Task({
      id: "child-enrichment",
      title: "Book Enrichment Classes",
      description: "Register for music, art, or educational programs"
    }),
    new Task({
      id: "parenting-classes",
      title: "Register for Parenting Workshop",
      description: "Sign up for parenting education sessions"
    }),
    new Task({
      id: "child-safety",
      title: "Schedule Child Safety Training",
      description: "Book first aid or safety certification courses"
    })
  ],
  "Mental Health & Counseling Services": [
    new Task({
      id: "therapy-intake",
      title: "Schedule Therapy Intake Session",
      description: "Book initial mental health consultation"
    }),
    new Task({
      id: "counseling-match",
      title: "Find Counselor Match",
      description: "Connect with compatible mental health professional"
    }),
    new Task({
      id: "support-group",
      title: "Join Support Group",
      description: "Register for group therapy sessions"
    }),
    new Task({
      id: "crisis-intervention",
      title: "Access Crisis Services",
      description: "Connect with emergency mental health support"
    }),
    new Task({
      id: "psychiatry-consult",
      title: "Schedule Psychiatric Consultation",
      description: "Book appointment for medication management"
    }),
    new Task({
      id: "teletherapy-setup",
      title: "Set Up Teletherapy Services",
      description: "Arrange virtual counseling sessions"
    }),
    new Task({
      id: "addiction-treatment",
      title: "Access Addiction Treatment",
      description: "Connect with substance abuse recovery services"
    }),
    new Task({
      id: "wellness-coaching",
      title: "Book Wellness Coach Session",
      description: "Schedule holistic mental health support"
    })
  ],
  "Housing & Real Estate Services": [
    new Task({
      id: "rental-application",
      title: "Submit Rental Application",
      description: "Process apartment or house rental application"
    }),
    new Task({
      id: "property-management",
      title: "Contact Property Management",
      description: "Reach out to property managers about maintenance or issues"
    }),
    new Task({
      id: "mortgage-pre-approval",
      title: "Request Mortgage Pre-Approval",
      description: "Start home loan pre-approval process"
    }),
    new Task({
      id: "home-inspection",
      title: "Schedule Home Inspection",
      description: "Book professional property inspection services"
    }),
    new Task({
      id: "real-estate-agent",
      title: "Find Real Estate Agent",
      description: "Connect with licensed real estate professionals"
    }),
    new Task({
      id: "title-search",
      title: "Request Property Title Search",
      description: "Verify property ownership and liens"
    }),
    new Task({
      id: "housing-assistance",
      title: "Apply for Housing Assistance",
      description: "Check eligibility for housing support programs"
    }),
    new Task({
      id: "eviction-prevention",
      title: "Access Eviction Prevention Services",
      description: "Connect with legal aid for housing stability"
    })
  ],
  "Food & Dining Services": [
    new Task({
      id: "restaurant-reservation",
      title: "Make Restaurant Reservation",
      description: "Book table at dining establishment"
    }),
    new Task({
      id: "catering-service",
      title: "Schedule Catering Service",
      description: "Arrange food service for events or gatherings"
    }),
    new Task({
      id: "food-delivery",
      title: "Coordinate Food Delivery",
      description: "Order meal delivery from restaurants"
    }),
    new Task({
      id: "dietary-consultation",
      title: "Schedule Dietary Consultation",
      description: "Book appointment with nutritionist or dietitian"
    }),
    new Task({
      id: "meal-prep-service",
      title: "Set Up Meal Prep Service",
      description: "Arrange regular prepared meal delivery"
    }),
    new Task({
      id: "food-allergy-alert",
      title: "Register Food Allergy Information",
      description: "Notify restaurants of dietary restrictions"
    }),
    new Task({
      id: "cooking-class",
      title: "Book Cooking Class",
      description: "Register for culinary instruction sessions"
    }),
    new Task({
      id: "food-tour",
      title: "Schedule Food Tour",
      description: "Book guided culinary experience tours"
    })
  ],
  "Fitness & Wellness Services": [
    new Task({
      id: "personal-training",
      title: "Schedule Personal Training",
      description: "Book one-on-one fitness instruction"
    }),
    new Task({
      id: "gym-membership",
      title: "Set Up Gym Membership",
      description: "Process fitness center enrollment"
    }),
    new Task({
      id: "fitness-class",
      title: "Book Fitness Class",
      description: "Register for group exercise sessions"
    }),
    new Task({
      id: "wellness-coach",
      title: "Schedule Wellness Coach",
      description: "Book holistic health and wellness consultation"
    }),
    new Task({
      id: "sports-facility-reserve",
      title: "Reserve Sports Facility",
      description: "Book court or field for athletic activities"
    }),
    new Task({
      id: "fitness-assessment",
      title: "Schedule Fitness Assessment",
      description: "Book physical fitness evaluation"
    }),
    new Task({
      id: "meditation-class",
      title: "Join Meditation Class",
      description: "Register for guided meditation sessions"
    }),
    new Task({
      id: "yoga-instruction",
      title: "Schedule Yoga Session",
      description: "Book private or group yoga instruction"
    })
  ]
}

/**
 * Exports all tasks as a structured JSON object for vector search indexing
 * @returns A JSON string representation of all tasks with their categories
 */
export function getAllTasksAsJson(): string {
  const allTasks: { id: string; title: string; description: string; category: string; additional_metadata?: string }[] =
    []

  // Convert the tasks record into a flat array with category included
  Object.entries(tasks).forEach(([category, taskList]) => {
    taskList.forEach((task) => {
      allTasks.push({
        id: task.id,
        title: task.title,
        description: task.description,
        category,
        additional_metadata: task.additional_metadata || undefined
      })
    })
  })

  return JSON.stringify(allTasks)
}
